import React, { useState, useEffect, useRef } from 'react';
import {
  MessageCircle,
  Send,
  Edit3,
  CheckCircle,
  Clock,
  Brain,
  Sparkles,
  User,
  Heart
} from 'lucide-react';
import SectionControls from '../shared/SectionControls';

const RecommendationsAssistant = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [isInView, setIsInView] = useState(false);
  const [relationshipLevel, setRelationshipLevel] = useState(0);
  const [currentStage, setCurrentStage] = useState('slider'); // slider, questions, processing, letter, complete
  const [messages, setMessages] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [processingSteps, setProcessingSteps] = useState([]);
  const [letterContent, setLetterContent] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [rightSideContent, setRightSideContent] = useState('preview');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => startAnimation(), 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const getQuestionsForLevel = (level) => {
    if (level <= 30) {
      // Newer relationship
      return [
        "What's one thing that went well in Mrs. LaSalle's class?",
        "Any specific moment she helped you understand something?",
        "What would you want her to know about your goals?"
      ];
    } else if (level <= 70) {
      // Medium relationship
      return [
        "When did Mrs. LaSalle see you struggle then succeed?",
        "What's your biggest accomplishment in her class?",
        "Any moment that showed your character to her?"
      ];
    } else {
      // Close relationship
      return [
        "What moment would surprise Mrs. LaSalle about you outside class?",
        "Any inside jokes or memorable conversations with her?",
        "What would she say is your unique strength?"
      ];
    }
  };

  const getLetterTone = (level) => {
    if (level <= 30) {
      return {
        greeting: "Dear Mrs. LaSalle,",
        opening: "I know we haven't worked together for very long, but I really valued our time in your class. I'm reaching out because I'm hoping you might consider writing a college recommendation letter for me.",
        style: "formal"
      };
    } else if (level <= 70) {
      return {
        greeting: "Dear Mrs. LaSalle,",
        opening: "I hope you're doing well! I've really appreciated having you as a teacher this year. I'm starting my college applications and was wondering if you'd be willing to write a recommendation letter for me.",
        style: "warm-professional"
      };
    } else {
      return {
        greeting: "Hey Mrs. LaSalle,",
        opening: "I hope you're having a great week! You know how much your class has meant to me this year. I'm finally diving into college applications (I know, I know, you told me to start earlier 😅), and I was really hoping you'd be willing to write my recommendation letter.",
        style: "casual-warm"
      };
    }
  };

  const startAnimation = () => {
    // Reset states
    setRelationshipLevel(0);
    setCurrentStage('slider');
    setMessages([]);
    setCurrentQuestionIndex(0);
    setProcessingSteps([]);
    setLetterContent('');
    setIsTyping(false);
    setRightSideContent('preview');

    // Stage 1: Slider interaction (0-2000ms total) - SLOWER for readability
    setTimeout(() => {
      // Animate slider to different positions - slower for better visibility
      setTimeout(() => setRelationshipLevel(25), 400);  // First position
      setTimeout(() => setRelationshipLevel(60), 1200);  // Second position
      setTimeout(() => setRelationshipLevel(85), 2000);  // Final position
    }, 100);

    // Stage 2: Questions phase (2500ms start) - After slider completes
    setTimeout(() => {
      setCurrentStage('questions');
      setRightSideContent('analysis');
      const questions = getQuestionsForLevel(85);

      // Add AI introduction
      setMessages([{
        type: 'ai',
        content: 'Perfect! Since you have a close relationship with Mrs. LaSalle, I can help you write a more personal request. Let me ask you a few quick questions.',
        timestamp: Date.now()
      }]);

      // Add questions progressively - 2000ms per Q&A pair for better readability
      questions.forEach((question, index) => {
        setTimeout(() => {
          setMessages(prev => [...prev, {
            type: 'ai',
            content: question,
            timestamp: Date.now()
          }]);

          // Add student response after 800ms (more time to read question)
          setTimeout(() => {
            const responses = [
              "She probably remembers when I stayed after every Tuesday to debug my bridge robot sensors. I was so frustrated but she never made me feel dumb for asking the same questions.",
              "Definitely when my robot completely failed at the state competition but she came over and said 'That's the most educational failure I've ever seen' and helped me turn it into a learning moment.",
              "She'd probably say I'm way more persistent than I look. Like when I spent three weeks trying to get that one sensor to work properly."
            ];

            setMessages(prev => [...prev, {
              type: 'student',
              content: responses[index],
              timestamp: Date.now()
            }]);
          }, 800); // 800ms after question appears
        }, (2000 * index) + 400); // 2000ms between Q&A pairs
      });
    }, 2500); // Start after slider completes

    // Stage 3: AI Processing - Adjusted timing for new flow
    setTimeout(() => {
      setCurrentStage('processing');
      setRightSideContent('insights');
      const steps = [
        'Analyzing your close mentor relationship...',
        'Incorporating specific moments and memories...',
        'Crafting personal, authentic tone...'
      ];

      steps.forEach((step, index) => {
        setTimeout(() => {
          setProcessingSteps(prev => [...prev, step]);
        }, 200 * index); // Slightly slower for visibility
      });
    }, 9000); // Start after questions complete (2500 + 3*2000 + buffer)

    // Stage 4: Letter generation - MUCH FASTER typing
    setTimeout(() => {
      setCurrentStage('letter');
      setRightSideContent('letter');
      setIsTyping(true);

      const letterTone = getLetterTone(85);
      // Shorter, more focused letter for better demo experience
      const fullLetter = `${letterTone.greeting}

${letterTone.opening}

I know you remember those Tuesday afternoons when I'd stay after class, completely frustrated with my bridge robot sensors. You never made me feel dumb for asking the same questions over and over - instead, you helped me see that persistence was actually one of my strengths.

You've seen me at my most frustrated and my most determined. That curiosity and persistence you've witnessed firsthand would mean so much coming from you in a recommendation.

Would you be willing to write a letter for me?

Thanks for everything,
Sarah`;

      // Typewriter effect - MUCH faster for better viewing experience
      let currentIndex = 0;
      const typeInterval = setInterval(() => {
        if (currentIndex < fullLetter.length) {
          // Type 2-3 characters at a time for faster reveal
          const charsToAdd = Math.min(3, fullLetter.length - currentIndex);
          currentIndex += charsToAdd;
          setLetterContent(fullLetter.substring(0, currentIndex));
        } else {
          clearInterval(typeInterval);
          setIsTyping(false);
          setTimeout(() => setCurrentStage('complete'), 300);
        }
      }, 8); // Much faster - 8ms per 3 characters
    }, 10000); // Start after processing completes
  };

  const getSliderColor = () => {
    if (relationshipLevel <= 30) return 'from-red-300 to-orange-300';
    if (relationshipLevel <= 70) return 'from-orange-300 to-green-300';
    return 'from-green-300 to-emerald-400';
  };

  const getSliderLabel = () => {
    if (relationshipLevel <= 30) return 'Just started working together';
    if (relationshipLevel <= 70) return 'Know each other pretty well';
    return 'Close mentor relationship';
  };

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-6 lg:mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-50 rounded-full">
            <Heart className="w-4 h-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">
              Recommendations Assistant
            </span>
          </div>
        </div>
        <h2 className="text-2xl lg:text-4xl font-bold text-slate-900 mb-2 lg:mb-4">
          Personal Letters, Not Templates
        </h2>
        <p className="text-lg lg:text-xl text-slate-600 max-w-3xl mx-auto">
          Tell us about your relationship with your teacher, answer a few quick questions,
          and we'll help you write a personalized request that actually sounds like you.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Left Column - Interface */}
        <div className="space-y-6">
          {/* Relationship Slider */}
          {currentStage === 'slider' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Great! I see you want to work with Mrs. LaSalle.</h3>
                  <p className="text-sm text-slate-600">Let me understand your relationship with them using the slider below.</p>
                </div>
              </div>

              <div className="bg-violet-50 rounded-xl p-6">
                <h4 className="text-center text-violet-700 font-medium mb-6">
                  How well does Mrs. LaSalle know you?
                </h4>

                <div className="relative mb-6">
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full bg-gradient-to-r ${getSliderColor()} transition-all duration-500`}
                      style={{ width: `${relationshipLevel}%` }}
                    />
                  </div>
                  <div
                    className="absolute top-1/2 transform -translate-y-1/2 w-6 h-6 bg-violet-600 rounded-full shadow-lg transition-all duration-500"
                    style={{ left: `calc(${relationshipLevel}% - 12px)` }}
                  />
                </div>

                <div className="flex justify-between text-xs text-slate-600">
                  <span>Just started working together</span>
                  <span>Know each other pretty well</span>
                  <span>Close mentor relationship</span>
                </div>

                <div className="text-center mt-4">
                  <span className="text-sm font-medium text-slate-700">
                    {getSliderLabel()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Chat Interface */}
          {currentStage === 'questions' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-violet-600 to-violet-700">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Quick Questions
                </h3>
              </div>

              <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 animate-fadeIn ${
                      message.type === 'student' ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.type === 'student'
                        ? 'bg-violet-100'
                        : 'bg-violet-600'
                    }`}>
                      {message.type === 'student' ? (
                        <User className="w-4 h-4 text-violet-600" />
                      ) : (
                        <Brain className="w-4 h-4 text-white" />
                      )}
                    </div>

                    <div className={`flex-1 ${message.type === 'student' ? 'text-right' : ''}`}>
                      <div className={`inline-block px-4 py-2 rounded-lg max-w-[85%] ${
                        message.type === 'student'
                          ? 'bg-violet-100 text-violet-900'
                          : 'bg-slate-100 text-slate-900'
                      }`}>
                        {message.content}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Processing/Status Interface - Show during processing AND letter generation */}
          {(currentStage === 'processing' || currentStage === 'letter' || currentStage === 'complete') && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  currentStage === 'complete' ? 'bg-emerald-100' : 'bg-violet-100'
                }`}>
                  {currentStage === 'complete' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : currentStage === 'letter' ? (
                    <Edit3 className="w-5 h-5 text-violet-600" />
                  ) : (
                    <Brain className="w-5 h-5 text-violet-600 animate-pulse" />
                  )}
                </div>
                <h3 className="font-semibold text-slate-900">
                  {currentStage === 'complete' ? 'Letter Complete!' :
                   currentStage === 'letter' ? 'Writing your letter...' :
                   'Processing your responses...'}
                </h3>
              </div>

              <div className="space-y-3">
                {processingSteps.map((step, index) => (
                  <div key={index} className="flex items-center gap-3 animate-fadeIn">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-slate-700">{step}</span>
                  </div>
                ))}

                {/* Show progress during letter generation */}
                {currentStage === 'letter' && (
                  <div className="mt-4 p-3 bg-violet-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-violet-600 rounded-full animate-pulse" />
                      <span className="text-sm text-violet-700 font-medium">Writing in progress...</span>
                    </div>
                    <div className="text-xs text-violet-600">
                      Crafting personalized language based on your relationship
                    </div>
                  </div>
                )}

                {/* Success state */}
                {currentStage === 'complete' && (
                  <div className="mt-4 p-4 bg-emerald-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-700 font-medium">
                        Your personalized letter is ready to send!
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Dynamic Content */}
        <div className="space-y-6">
          {/* Process Preview (Stage 1) */}
          {rightSideContent === 'preview' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                What happens next
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center text-xs font-bold text-violet-600">1</div>
                  <span className="text-sm text-slate-700">Quick relationship questions</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center text-xs font-bold text-violet-600">2</div>
                  <span className="text-sm text-slate-700">AI analyzes your responses</span>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-6 h-6 bg-violet-100 rounded-full flex items-center justify-center text-xs font-bold text-violet-600">3</div>
                  <span className="text-sm text-slate-700">Personal letter generated</span>
                </div>
              </div>
            </div>
          )}

          {/* Live Analysis (Stage 2) */}
          {rightSideContent === 'analysis' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-600" />
                Live Analysis
              </h3>
              <div className="space-y-4">
                <div className="p-3 bg-violet-50 rounded-lg">
                  <div className="text-xs text-violet-600 font-medium mb-1">Detected Themes</div>
                  <div className="flex flex-wrap gap-1">
                    <span className="px-2 py-1 bg-violet-200 text-violet-800 text-xs rounded">Persistence</span>
                    <span className="px-2 py-1 bg-violet-200 text-violet-800 text-xs rounded">Problem-solving</span>
                    <span className="px-2 py-1 bg-violet-200 text-violet-800 text-xs rounded">Growth mindset</span>
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <div className="text-xs text-emerald-600 font-medium mb-1">Specific Moments</div>
                  <ul className="text-xs text-emerald-700 space-y-1">
                    <li>• Tuesday after-school debugging sessions</li>
                    <li>• State competition failure → learning</li>
                    <li>• Three weeks on one sensor</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Relationship Insights (Stage 3) */}
          {rightSideContent === 'insights' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5 text-violet-600" />
                Relationship Insights
              </h3>
              <div className="space-y-4">
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <div className="text-sm font-medium text-emerald-800 mb-2">Close Mentor Relationship</div>
                  <div className="text-xs text-emerald-700">✓ Casual, warm tone appropriate</div>
                  <div className="text-xs text-emerald-700">✓ Can reference specific shared memories</div>
                  <div className="text-xs text-emerald-700">✓ Teacher knows student's character deeply</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <div className="text-xs text-blue-600 font-medium mb-1">Letter Strategy</div>
                  <div className="text-xs text-blue-700">Start with shared memory → character growth → specific request</div>
                </div>
              </div>
            </div>
          )}

          {/* Letter Generation (Stage 4-5) */}
          {rightSideContent === 'letter' && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className={`px-6 py-4 transition-all duration-500 ${
                currentStage === 'complete'
                  ? 'bg-gradient-to-r from-emerald-600 to-emerald-700'
                  : 'bg-gradient-to-r from-violet-600 to-violet-700'
              }`}>
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Edit3 className="w-5 h-5" />
                  {currentStage === 'complete' ? 'Ready to Send' : 'Generating Letter...'}
                </h3>
              </div>

              <div className="p-6">
                <div className="bg-slate-50 rounded-lg p-4 font-mono text-sm leading-relaxed whitespace-pre-wrap">
                  {letterContent}
                  {isTyping && <span className="animate-pulse">|</span>}
                </div>

                {currentStage === 'complete' && (
                  <div className="mt-6 space-y-4">
                    <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">
                          Personal, authentic, ready to edit
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-lg font-bold text-slate-900">847</div>
                        <div className="text-xs text-slate-600">Words (optimal)</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-lg font-bold text-slate-900">12</div>
                        <div className="text-xs text-slate-600">Specific examples</div>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <div className="text-lg font-bold text-slate-900">3</div>
                        <div className="text-xs text-slate-600">Personal moments</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>

      {/* Section Controls */}
      <SectionControls
        onReplay={() => {
          setIsInView(false);
          setTimeout(() => {
            setIsInView(true);
            startAnimation();
          }, 100);
        }}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={currentStage === 'complete'}
      />
    </div>
  );
};

export default RecommendationsAssistant;